import '../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled, { keyframes } from 'styled-components';
import Hls from 'hls.js';
import { FiChevronLeft, FiChevronRight, FiCopy, FiDownload, FiX } from 'react-icons/fi';
import { LuScanText } from 'react-icons/lu';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import Modal from './Modal';
import { copyImageFromSourceChain, fetchPreferredImageBlob } from '../../util/imageClipboard';
import { getNextMediaSource, getUniqueMediaSources } from '../../util/url-meta-media';
import { downloadBlobInBrowser, getFileExtension, isTauriEnvironment, saveBlobWithTauri } from '../../util/downloadHelpers';
import { buildZipDownloadFileName, createZipBlob } from '../../util/createZip';

const modalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: '100vw',
        height: '100vh',
        border: 'none',
        borderRadius: 0,
        padding: 0,
        background: 'transparent',
        overflow: 'hidden'
    }
};

const ENTER_EASING = 'cubic-bezier(0.23, 1, 0.32, 1)';
const SLIDE_ANIMATION_MS = 280;
const SLIDE_OFFSET_PX = 56;

const stopEvent = event => {
    if (!event) {
        return;
    }

    event.stopPropagation?.();
    event.preventDefault?.();
    event.nativeEvent?.stopImmediatePropagation?.();
};

const stopPrimaryMouseDown = event => {
    if (!event) {
        return;
    }

    if (typeof event.button === 'number' && event.button !== 0) {
        event.stopPropagation?.();
        return;
    }

    stopEvent(event);
};

const IMAGE_FILE_EXTENSIONS = new Set([
    'avif', 'bmp', 'gif', 'heic', 'heif', 'jpeg', 'jpg', 'png', 'svg', 'tif', 'tiff', 'webp'
]);

const getFileNameFromSource = source => {
    if (!source || typeof source !== 'string') {
        return '';
    }

    try {
        const url = new URL(source);
        const pathname = url.pathname || '';
        return pathname.split('/').pop() || '';
    } catch (error) {
        return source.split('/').pop()?.split('?')[0] || '';
    }
};

const inferExtensionFromBlob = blob => {
    const type = String(blob?.type || '').toLowerCase();
    if (type === 'image/jpeg' || type === 'image/jpg') return 'jpg';
    if (type === 'image/png') return 'png';
    if (type === 'image/gif') return 'gif';
    if (type === 'image/webp') return 'webp';
    if (type === 'image/avif') return 'avif';
    if (type === 'image/svg+xml') return 'svg';
    if (type === 'image/bmp') return 'bmp';
    if (type === 'image/tiff') return 'tiff';
    if (type === 'image/heic' || type === 'image/heif') return 'heic';
    return '';
};

const inferMediaExtensionFromBlob = blob => {
    const imageExtension = inferExtensionFromBlob(blob);
    if (imageExtension) return imageExtension;

    const type = String(blob?.type || '').toLowerCase();
    if (type === 'video/mp4') return 'mp4';
    if (type === 'video/webm') return 'webm';
    if (type === 'video/quicktime') return 'mov';
    if (type === 'video/x-matroska') return 'mkv';
    return '';
};

const buildDownloadFileName = (preferredName, source, blob) => {
    const explicitName = String(preferredName || '').trim();
    const sourceName = getFileNameFromSource(source);
    const baseName = explicitName || sourceName || 'image';
    const currentExtension = getFileExtension(baseName).toLowerCase();
    const sourceExtension = getFileExtension(sourceName).toLowerCase();
    const inferredExtension = inferExtensionFromBlob(blob)
        || (IMAGE_FILE_EXTENSIONS.has(sourceExtension) ? sourceExtension : 'jpg');

    if (IMAGE_FILE_EXTENSIONS.has(currentExtension)) {
        return baseName;
    }

    return inferredExtension ? `${baseName}.${inferredExtension}` : baseName;
};

const fetchPreferredMediaBlob = async sourceChain => {
    const sources = Array.isArray(sourceChain) ? sourceChain.filter(Boolean) : [];

    for (const source of sources) {
        try {
            const response = await fetch(source);
            if (!response.ok) continue;

            const blob = await response.blob();
            if (blob?.type?.startsWith?.('image/') || blob?.type?.startsWith?.('video/')) {
                return { blob, source };
            }
        } catch (_error) {
            // Try the next source.
        }
    }

    return null;
};

const buildMediaDownloadFileName = (preferredName, source, blob, fallbackName) => {
    const explicitName = String(preferredName || fallbackName || '').trim();
    const sourceName = getFileNameFromSource(source);
    const baseName = explicitName || sourceName || fallbackName || 'media';
    const currentExtension = getFileExtension(baseName).toLowerCase();
    const sourceExtension = getFileExtension(sourceName).toLowerCase();
    const inferredExtension = inferMediaExtensionFromBlob(blob) || sourceExtension || 'mp4';

    return IMAGE_FILE_EXTENSIONS.has(currentExtension) || ['mp4', 'webm', 'mov', 'mkv'].includes(currentExtension)
        ? baseName
        : `${baseName}.${inferredExtension}`;
};

function LightboxVideo({ src, hlsUrl, poster, mediaKey, videoPositionsRef, sourceChain }) {
    const {t: lnT} = useTranslation();

    const videoRef = React.useRef(null);
    const [failedHls, setFailedHls] = React.useState(false);
    const [currentSrc, setCurrentSrc] = React.useState(src || '');
    const [showUnavailable, setShowUnavailable] = React.useState(!src && !hlsUrl);
    const failedSourcesRef = React.useRef(new Set());

    React.useEffect(() => {
        setCurrentSrc(src || '');
        setFailedHls(false);
        setShowUnavailable(!src && !hlsUrl);
        failedSourcesRef.current = new Set();
    }, [src, hlsUrl]);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video || !hlsUrl || failedHls) {
            return undefined;
        }

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = hlsUrl;
            return undefined;
        }

        if (!Hls.isSupported()) {
            return undefined;
        }

        const hls = new Hls({ enableWorker: true });
        hls.on(Hls.Events.ERROR, (_event, data) => {
            if (data?.fatal) {
                setFailedHls(true);
                try { hls.destroy(); } catch { }
            }
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);

        return () => {
            try { hls.destroy(); } catch { }
        };
    }, [hlsUrl, failedHls]);

    const savePosition = React.useCallback(() => {
        const video = videoRef.current;
        if (!video || !mediaKey || !videoPositionsRef) {
            return;
        }
        if (Number.isFinite(video.currentTime) && video.currentTime > 0) {
            videoPositionsRef.current.set(mediaKey, video.currentTime);
        }
    }, [lnT, mediaKey, videoPositionsRef]);

    React.useEffect(() => savePosition, [savePosition]);

    const handleVideoError = React.useCallback((event) => {
        if (hlsUrl && !failedHls) {
            failedSourcesRef.current.add(hlsUrl);
            setFailedHls(true);
            return;
        }

        const failedSource = event.currentTarget?.currentSrc || currentSrc || src || hlsUrl;
        if (failedSource) {
            failedSourcesRef.current.add(failedSource);
        }

        let nextFallback = getNextMediaSource(failedSource, sourceChain);
        while (nextFallback && failedSourcesRef.current.has(nextFallback)) {
            nextFallback = getNextMediaSource(nextFallback, sourceChain);
        }

        if (nextFallback && nextFallback !== currentSrc) {
            setCurrentSrc(nextFallback);
            setShowUnavailable(false);
            return;
        }

        setShowUnavailable(true);
    }, [lnT, currentSrc, failedHls, hlsUrl, sourceChain, src]);

    if (showUnavailable) {
        return (
            <UnavailableState>{lnT('messages:previewUnavailable')}</UnavailableState>
        );
    }

    return (
        <SlideVideoWrapper>
            <SlideVideo
                ref={videoRef}
                key={(hlsUrl && !failedHls) ? hlsUrl : currentSrc}
                src={(hlsUrl && !failedHls) ? undefined : currentSrc}
                poster={poster || undefined}
                controls
                playsInline
                preload="metadata"
                referrerPolicy="no-referrer"
                onClick={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
                onLoadedMetadata={event => {
                    const savedTime = mediaKey ? videoPositionsRef?.current?.get(mediaKey) : null;
                    if (Number.isFinite(savedTime) && savedTime > 0 && savedTime < event.currentTarget.duration) {
                        event.currentTarget.currentTime = savedTime;
                    }
                }}
                onTimeUpdate={savePosition}
                onPause={savePosition}
                onError={handleVideoError}
            />
        </SlideVideoWrapper>
    );
}

function ZoomableSlide({ view, videoPositionsRef }) {
    const {t: lnT} = useTranslation();

    const [imageSrc, setImageSrc] = React.useState(view?.src || '');
    const [showUnavailable, setShowUnavailable] = React.useState(Boolean(view?.unavailable || !view?.src));
    const failedSourcesRef = React.useRef(new Set());
    const transformRef = React.useRef(null);

    React.useEffect(() => {
        setImageSrc(view?.src || '');
        setShowUnavailable(Boolean(view?.unavailable || !view?.src));
        failedSourcesRef.current = new Set();
    }, [view]);

    if (view?.type === 'video' && view?.src) {
        return (
            <LightboxVideo
                src={view.src}
                hlsUrl={view.hlsUrl}
                poster={view.poster || undefined}
                mediaKey={view.hlsUrl || view.src}
                videoPositionsRef={videoPositionsRef}
                sourceChain={view.sourceChain}
            />
        );
    }

    if (showUnavailable || !imageSrc) {
        return (
            <UnavailableState>
                {view?.unavailableLabel || lnT('messages:previewUnavailable')}
            </UnavailableState>
        );
    }

    return (
        <TransformWrapper
            ref={transformRef}
            key={view?.src || 'lightbox-slide'}
            initialScale={1}
            minScale={0.5}
            maxScale={16}
            centerOnInit
            wheel={{
                step: 0.4,
                smoothStep: 0.005,
                wheelEnabled: true,
                touchPadEnabled: true,
                pinch: true
            }}
            pinch={{
                step: 5,
                enabled: true,
                scalePadding: 0.2,
                velocityDisabled: false,
                velocityScale: 1
            }}
            doubleClick={{
                disabled: false,
                step: 1.8
            }}
        >
            <TransformComponent
                wrapperStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                contentStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <SlideImage
                    src={imageSrc}
                    alt=""
                    onLoad={() => {
                        // centerOnInit measures content size at mount, before this async
                        // image (width/height: auto) has any intrinsic size - re-center
                        // once it actually loads so it doesn't stay positioned/scaled
                        // against a stale zero-size box.
                        transformRef.current?.centerView(1, 0);
                    }}
                    onError={event => {
                        const currentSource = event.currentTarget?.src || imageSrc;
                        failedSourcesRef.current.add(currentSource);

                        let nextFallback = getNextMediaSource(currentSource, view?.sourceChain);
                        while (nextFallback && failedSourcesRef.current.has(nextFallback)) {
                            nextFallback = getNextMediaSource(nextFallback, view?.sourceChain);
                        }

                        if (nextFallback && event.currentTarget.src !== nextFallback) {
                            setImageSrc(nextFallback);
                            setShowUnavailable(false);
                            return;
                        }

                        setShowUnavailable(true);
                    }}
                    onContextMenu={async event => {
                        event.preventDefault();
                        event.stopPropagation?.();

                        try {
                            await copyImageFromSourceChain(view?.sourceChain || [imageSrc]);
                        } catch (error) {
                            console.error('Failed to copy image from lightbox:', error);
                        }
                    }}
                />
            </TransformComponent>
        </TransformWrapper>
    );
}

function MediaLightboxModal({
    isOpen,
    onClose,
    views = [],
    initialIndex = 0,
    ocrText = null,
    onExtractAllOcr = null,
    extractingAllOcr = false,
    allOcrExtracted = false,
    hideDownload = false
}) {
    const {t: lnT} = useTranslation();

    const safeViews = React.useMemo(
        () => (
            Array.isArray(views)
                ? views
                    .filter(Boolean)
                    .map(view => {
                        const sourceChain = Array.isArray(view?.sourceChain) && view.sourceChain.length > 0
                            ? getUniqueMediaSources(view.sourceChain)
                            : getUniqueMediaSources([
                                view?.originalSrc,
                                view?.src,
                                view?.fallbackUrl
                            ]);

                        return {
                            ...view,
                            sourceChain,
                            fallbackUrl: view?.fallbackUrl || sourceChain[1] || null
                        };
                    })
                : []
        ),
        [lnT, views]
    );
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [transitionState, setTransitionState] = React.useState(null);
    const [copyState, setCopyState] = React.useState('idle');
    const [downloadState, setDownloadState] = React.useState('idle');
    const [downloadAllState, setDownloadAllState] = React.useState('idle');
    const [ocrCopyState, setOcrCopyState] = React.useState('idle');
    const [allOcrCopyState, setAllOcrCopyState] = React.useState('idle');
    const ocrCopyTimeoutRef = React.useRef(null);
    const allOcrCopyTimeoutRef = React.useRef(null);
    const touchStartXRef = React.useRef(null);
    const videoPositionsRef = React.useRef(new Map());
    const animationFrameRef = React.useRef(null);
    const transitionTimeoutRef = React.useRef(null);
    const copyTimeoutRef = React.useRef(null);
    const downloadTimeoutRef = React.useRef(null);
    const downloadAllTimeoutRef = React.useRef(null);
    const isTauri = isTauriEnvironment();
    const activeView = safeViews[currentIndex] || null;
    const activeViewIsImage = activeView?.type !== 'video';
    const hasPerViewOcr = safeViews.some(view => String(view?.ocrText || '').trim());
    const activeOcrText = String(
        activeView?.ocrText || (hasPerViewOcr ? '' : ocrText) || ''
    ).trim();
    const allOcrText = React.useMemo(() => {
        const seen = new Set();
        const parts = safeViews.reduce((result, view, index) => {
            const text = String(view?.ocrText || '').trim();
            if (!text || seen.has(text)) {
                return result;
            }

            seen.add(text);
            result.push(`Image ${index + 1}\n${text}`);
            return result;
        }, []);

        return parts.length > 0 ? parts.join('\n\n') : String(ocrText || '').trim();
    }, [lnT, ocrText, safeViews]);

    const clearPendingAnimation = React.useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
            transitionTimeoutRef.current = null;
        }
    }, [lnT]);

    React.useEffect(() => clearPendingAnimation, [clearPendingAnimation]);

    React.useEffect(() => () => {
        if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
        }
        if (downloadTimeoutRef.current) {
            clearTimeout(downloadTimeoutRef.current);
        }
        if (downloadAllTimeoutRef.current) {
            clearTimeout(downloadAllTimeoutRef.current);
        }
        if (ocrCopyTimeoutRef.current) {
            clearTimeout(ocrCopyTimeoutRef.current);
        }
        if (allOcrCopyTimeoutRef.current) {
            clearTimeout(allOcrCopyTimeoutRef.current);
        }
    }, []);

    const handleCopyOcr = React.useCallback(() => {
        const text = activeOcrText;
        if (!text || ocrCopyState === 'working') return;
        setOcrCopyState('working');
        navigator.clipboard.writeText(text).then(() => {
            setOcrCopyState('done');
        }).catch(() => {
            setOcrCopyState('error');
        }).finally(() => {
            if (ocrCopyTimeoutRef.current) clearTimeout(ocrCopyTimeoutRef.current);
            ocrCopyTimeoutRef.current = setTimeout(() => {
                setOcrCopyState('idle');
                ocrCopyTimeoutRef.current = null;
            }, 1800);
        });
    }, [lnT, activeOcrText, ocrCopyState]);

    const handleCopyAllOcr = React.useCallback(() => {
        if (!allOcrText || allOcrCopyState === 'working') return;
        setAllOcrCopyState('working');
        navigator.clipboard.writeText(allOcrText).then(() => {
            setAllOcrCopyState('done');
        }).catch(() => {
            setAllOcrCopyState('error');
        }).finally(() => {
            if (allOcrCopyTimeoutRef.current) clearTimeout(allOcrCopyTimeoutRef.current);
            allOcrCopyTimeoutRef.current = setTimeout(() => {
                setAllOcrCopyState('idle');
                allOcrCopyTimeoutRef.current = null;
            }, 1800);
        });
    }, [lnT, allOcrCopyState, allOcrText]);

    React.useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        clearPendingAnimation();
        const nextIndex = Number.isFinite(initialIndex)
            ? Math.min(Math.max(initialIndex, 0), Math.max(safeViews.length - 1, 0))
            : 0;
        setCurrentIndex(nextIndex);
        setTransitionState(null);
    }, [clearPendingAnimation, initialIndex, isOpen, safeViews.length]);

    const closeModal = React.useCallback(
        event => {
            stopEvent(event);
            clearPendingAnimation();
            onClose?.(event);
        },
        [lnT, clearPendingAnimation, onClose]
    );

    const startTransitionToIndex = React.useCallback(
        nextIndex => {
            if (!Number.isFinite(nextIndex)) {
                return;
            }

            const clampedIndex = Math.min(Math.max(nextIndex, 0), Math.max(safeViews.length - 1, 0));
            if (clampedIndex === currentIndex || transitionState) {
                return;
            }

            const direction = clampedIndex > currentIndex ? 1 : -1;
            clearPendingAnimation();
            setTransitionState({
                fromIndex: currentIndex,
                toIndex: clampedIndex,
                direction,
                phase: 'idle'
            });

            animationFrameRef.current = requestAnimationFrame(() => {
                setTransitionState(previousState => (
                    previousState
                        ? {
                            ...previousState,
                            phase: 'active'
                        }
                        : previousState
                ));
                animationFrameRef.current = null;
            });

            transitionTimeoutRef.current = setTimeout(() => {
                setCurrentIndex(clampedIndex);
                setTransitionState(null);
                transitionTimeoutRef.current = null;
            }, SLIDE_ANIMATION_MS);
        },
        [lnT, clearPendingAnimation, currentIndex, safeViews.length, transitionState]
    );

    const showIndexImmediately = React.useCallback(
        nextIndex => {
            if (!Number.isFinite(nextIndex)) {
                return;
            }

            const clampedIndex = Math.min(Math.max(nextIndex, 0), Math.max(safeViews.length - 1, 0));
            const displayedIndex = transitionState?.toIndex ?? currentIndex;
            if (clampedIndex === displayedIndex) {
                return;
            }

            clearPendingAnimation();
            setTransitionState(null);
            setCurrentIndex(clampedIndex);
        },
        [lnT, clearPendingAnimation, currentIndex, safeViews.length, transitionState?.toIndex]
    );

    const handleThumbnailRailScrub = React.useCallback(
        event => {
            event.stopPropagation?.();

            const rail = event.currentTarget;
            const thumbnailButtons = Array.from(rail.querySelectorAll('[data-thumbnail-index]'));
            if (!thumbnailButtons.length || !Number.isFinite(event.clientX)) {
                return;
            }

            const closestButton = thumbnailButtons.reduce((closest, button) => {
                const rect = button.getBoundingClientRect();
                const centerX = rect.left + (rect.width / 2);
                const distance = Math.abs(event.clientX - centerX);

                return !closest || distance < closest.distance
                    ? { button, distance }
                    : closest;
            }, null);

            const nextIndex = Number(closestButton?.button?.getAttribute('data-thumbnail-index'));
            showIndexImmediately(nextIndex);
        },
        [lnT, showIndexImmediately]
    );

    const goPrev = React.useCallback(() => {
        startTransitionToIndex(currentIndex - 1);
    }, [lnT, currentIndex, startTransitionToIndex]);

    const goNext = React.useCallback(() => {
        startTransitionToIndex(currentIndex + 1);
    }, [lnT, currentIndex, startTransitionToIndex]);

    const handleCopy = React.useCallback(async () => {
        if (copyState === 'working') {
            return;
        }

        if (!activeViewIsImage || !activeView?.sourceChain?.length) {
            setCopyState('error');
            return;
        }

        setCopyState('working');
        try {
            const copied = await copyImageFromSourceChain(activeView.sourceChain);
            setCopyState(copied ? 'done' : 'error');
        } catch (error) {
            setCopyState('error');
        }

        if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
        }
        copyTimeoutRef.current = setTimeout(() => {
            setCopyState('idle');
            copyTimeoutRef.current = null;
        }, 1800);
    }, [lnT, activeView, activeViewIsImage, copyState]);

    const handleDownload = React.useCallback(async () => {
        if (downloadState === 'working') {
            return;
        }

        if (!activeViewIsImage || !activeView?.sourceChain?.length) {
            setDownloadState('error');
            return;
        }

        setDownloadState('working');
        try {
            const fetched = await fetchPreferredImageBlob(activeView.sourceChain);
            if (!fetched?.blob) {
                throw new Error('No downloadable image source');
            }

            const fileName = buildDownloadFileName(activeView.fileName, fetched.source, fetched.blob);
            if (isTauri) {
                const saved = await saveBlobWithTauri(fetched.blob, fileName);
                if (!saved) {
                    setDownloadState('idle');
                    return;
                }
            } else {
                downloadBlobInBrowser(fetched.blob, fileName);
            }

            setDownloadState('done');
        } catch (error) {
            setDownloadState('error');
        }

        if (downloadTimeoutRef.current) {
            clearTimeout(downloadTimeoutRef.current);
        }
        downloadTimeoutRef.current = setTimeout(() => {
            setDownloadState('idle');
            downloadTimeoutRef.current = null;
        }, 1800);
    }, [lnT, activeView, activeViewIsImage, downloadState, isTauri]);

    const handleDownloadAll = React.useCallback(async () => {
        if (downloadAllState === 'working') {
            return;
        }

        const mediaViews = safeViews.filter(view => (
            Array.isArray(view?.sourceChain) && view.sourceChain.length > 0
        ));
        if (mediaViews.length < 2) {
            return;
        }

        setDownloadAllState('working');
        try {
            const files = [];
            for (let index = 0; index < mediaViews.length; index += 1) {
                const view = mediaViews[index];
                const fetched = await fetchPreferredMediaBlob(view.sourceChain);
                if (!fetched?.blob) {
                    continue;
                }

                const fileName = buildMediaDownloadFileName(
                    view.fileName,
                    fetched.source,
                    fetched.blob,
                    `carousel-${view.type === 'video' ? 'video' : 'image'}-${String(index + 1).padStart(2, '0')}`
                );
                files.push({
                    name: `${String(index + 1).padStart(2, '0')}-${fileName}`,
                    blob: fetched.blob
                });
            }

            if (files.length === 0) {
                throw new Error('No gallery media could be downloaded');
            }

            const zipBlob = await createZipBlob(files);
            const zipFileName = buildZipDownloadFileName(mediaViews[0]?.fileName);
            if (isTauri) {
                const saved = await saveBlobWithTauri(zipBlob, zipFileName);
                if (!saved) {
                    setDownloadAllState('idle');
                    return;
                }
            } else {
                downloadBlobInBrowser(zipBlob, zipFileName);
            }
            setDownloadAllState(files.length === mediaViews.length ? 'done' : 'error');
        } catch (error) {
            setDownloadAllState('error');
        }

        if (downloadAllTimeoutRef.current) {
            clearTimeout(downloadAllTimeoutRef.current);
        }
        downloadAllTimeoutRef.current = setTimeout(() => {
            setDownloadAllState('idle');
            downloadAllTimeoutRef.current = null;
        }, 1800);
    }, [lnT, downloadAllState, isTauri, safeViews]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = event => {
            if (event.key === 'Escape') {
                closeModal(event);
                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                goPrev();
                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                goNext();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [closeModal, goNext, goPrev, isOpen]);

    if (!isOpen) {
        return null;
    }

    const hasThumbnailRail = safeViews.length > 1;
    const downloadableMediaViews = safeViews.filter(view => (
        Array.isArray(view?.sourceChain) && view.sourceChain.length > 0
    ));
    const canDownloadAll = downloadableMediaViews.length > 1;
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < safeViews.length - 1;
    const activeThumbnailIndex = transitionState?.toIndex ?? currentIndex;
    const outgoingView = transitionState ? safeViews[transitionState.fromIndex] || null : null;
    const incomingView = transitionState ? safeViews[transitionState.toIndex] || null : null;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={closeModal}
            style={modalStyles}
            contentWrapperStyle={{
                width: '100%',
                height: '100%'
            }}
            contentLabel={lnT('messages:mediaLightbox')}
        >
            <LightboxShell
                data-media-lightbox-open="true"
                onTouchStart={event => {
                    touchStartXRef.current = event.changedTouches?.[0]?.clientX ?? null;
                }}
                onTouchEnd={event => {
                    const startX = touchStartXRef.current;
                    const endX = event.changedTouches?.[0]?.clientX ?? null;
                    touchStartXRef.current = null;

                    if (!Number.isFinite(startX) || !Number.isFinite(endX)) {
                        return;
                    }

                    const deltaX = endX - startX;
                    if (Math.abs(deltaX) < 50) {
                        return;
                    }

                    if (deltaX > 0) {
                        goPrev();
                    } else {
                        goNext();
                    }
                }}
            >
                <TopBar
                    onClick={event => {
                        event.stopPropagation?.();
                    }}
                    onMouseDown={event => {
                        event.stopPropagation?.();
                    }}
                >
                    <CounterLabel>
                        {safeViews.length > 0
                            ? lnT('messages:text_9b2e3898', { value1: String((transitionState?.toIndex ?? currentIndex) + 1), value2: String(safeViews.length) })
                            : '0 / 0'}
                    </CounterLabel>
                    <TopBarActions>
                        {canDownloadAll ? (
                            <TopActionButton
                                type="button"
                                disabled={downloadAllState === 'working'}
                                $disabled={downloadAllState === 'working'}
                                aria-disabled={downloadAllState === 'working'}
                                onClick={event => {
                                    event.stopPropagation?.();
                                    void handleDownloadAll();
                                }}
                                onMouseDown={event => {
                                    event.stopPropagation?.();
                                }}
                                aria-label={lnT('messages:downloadAllImages')}
                            >
                                <FiDownload size={16} />
                                <span>
                                    {downloadAllState === 'working'
                                        ? lnT('messages:downloading_f976eb28')
                                        : downloadAllState === 'done'
                                            ? lnT('messages:zipDownloaded')
                                            : downloadAllState === 'error'
                                                ? lnT('messages:someFailed')
                                                : lnT('messages:downloadZip')}
                                </span>
                            </TopActionButton>
                        ) : null}
                        {activeViewIsImage ? (
                            <>
                                {!hideDownload ? (
                                <TopActionButton
                                    type="button"
                                    disabled={!activeView?.sourceChain?.length || copyState === 'working'}
                                    $disabled={!activeView?.sourceChain?.length || copyState === 'working'}
                                    aria-disabled={!activeView?.sourceChain?.length || copyState === 'working'}
                                    onClick={event => {
                                        event.stopPropagation?.();
                                        void handleCopy();
                                    }}
                                    onMouseDown={event => {
                                        event.stopPropagation?.();
                                    }}
                                    aria-label={lnT('messages:copyImage')}
                                >
                                    <FiCopy size={16} />
                                    <span>
                                        {copyState === 'working'
                                            ? lnT('messages:copying')
                                            : copyState === 'done'
                                                ? lnT('messages:copied_8d525e5f')
                                                : copyState === 'error'
                                                    ? lnT('messages:copyFailed')
                                                    : lnT('common:copy')}
                                    </span>
                                </TopActionButton>
                                ) : null}
                                <TopActionButton
                                    type="button"
                                    disabled={!activeView?.sourceChain?.length || downloadState === 'working'}
                                    $disabled={!activeView?.sourceChain?.length || downloadState === 'working'}
                                    aria-disabled={!activeView?.sourceChain?.length || downloadState === 'working'}
                                    onClick={event => {
                                        event.stopPropagation?.();
                                        void handleDownload();
                                    }}
                                    onMouseDown={event => {
                                        event.stopPropagation?.();
                                    }}
                                    aria-label={lnT('messages:downloadImage_3ac575dc')}
                                >
                                    <FiDownload size={16} />
                                    <span>
                                        {downloadState === 'working'
                                            ? lnT('messages:downloading_f976eb28')
                                            : downloadState === 'done'
                                                ? lnT('messages:downloaded')
                                                : downloadState === 'error'
                                                    ? lnT('common:downloadFailed')
                                                    : lnT('messages:download')}
                                    </span>
                                </TopActionButton>
                            </>
                        ) : null}
                        <CloseButton
                            type="button"
                            onClick={event => {
                                event.stopPropagation?.();
                                closeModal(event);
                            }}
                            onMouseDown={event => {
                                event.stopPropagation?.();
                            }}
                            aria-label={lnT('messages:closeLightbox')}
                        >
                            <FiX size={22} />
                        </CloseButton>
                    </TopBarActions>
                </TopBar>

                <Stage onClick={closeModal} $hasThumbnailRail={hasThumbnailRail}>
                    <SlideViewport>
                        {transitionState ? (
                            <SlidesStack>
                                <SlidePane
                                    $direction={transitionState.direction}
                                    $phase={transitionState.phase}
                                    $role="outgoing"
                                >
                                    <SlideContent
                                        onClick={event => {
                                            stopEvent(event);
                                        }}
                                        onMouseDown={event => {
                                            stopPrimaryMouseDown(event);
                                        }}
                                        onContextMenu={event => {
                                            event.stopPropagation?.();
                                        }}
                                    >
                                        <ZoomableSlide view={outgoingView} videoPositionsRef={videoPositionsRef} />
                                    </SlideContent>
                                </SlidePane>
                                <SlidePane
                                    $direction={transitionState.direction}
                                    $phase={transitionState.phase}
                                    $role="incoming"
                                >
                                    <SlideContent
                                        onClick={event => {
                                            stopEvent(event);
                                        }}
                                        onMouseDown={event => {
                                            stopPrimaryMouseDown(event);
                                        }}
                                        onContextMenu={event => {
                                            event.stopPropagation?.();
                                        }}
                                    >
                                        <ZoomableSlide view={incomingView} videoPositionsRef={videoPositionsRef} />
                                    </SlideContent>
                                </SlidePane>
                            </SlidesStack>
                        ) : (
                            <SlidePane $direction={1} $phase="active" $role="current">
                                <SlideContent
                                    onClick={event => {
                                        stopEvent(event);
                                    }}
                                    onMouseDown={event => {
                                        stopPrimaryMouseDown(event);
                                    }}
                                    onContextMenu={event => {
                                        event.stopPropagation?.();
                                    }}
                                >
                                    <ZoomableSlide view={activeView} videoPositionsRef={videoPositionsRef} />
                                </SlideContent>
                            </SlidePane>
                        )}
                    </SlideViewport>

                    <NavLayer>
                        {canGoPrev ? (
                            <NavButton
                                type="button"
                                onClick={event => {
                                    stopEvent(event);
                                    goPrev();
                                }}
                                $side="left"
                                aria-label={lnT('messages:previousMedia')}
                            >
                                <FiChevronLeft size={26} />
                            </NavButton>
                        ) : null}

                        {canGoNext ? (
                            <NavButton
                                type="button"
                                onClick={event => {
                                    stopEvent(event);
                                    goNext();
                                }}
                                $side="right"
                                aria-label={lnT('messages:nextMedia')}
                            >
                                <FiChevronRight size={26} />
                            </NavButton>
                        ) : null}
                    </NavLayer>
                </Stage>

                {hasThumbnailRail ? (
                    <ThumbnailRail
                        onClick={event => { event.stopPropagation?.(); }}
                        onMouseDown={event => { event.stopPropagation?.(); }}
                        onMouseMove={handleThumbnailRailScrub}
                    >
                        {safeViews.map((view, viewIndex) => {
                            const thumbnailSrc = view?.poster || view?.src || view?.sourceChain?.[0] || '';
                            const isActive = viewIndex === activeThumbnailIndex;

                            return (
                                <ThumbnailButton
                                    key={`${thumbnailSrc || view?.fileName || 'media'}-${viewIndex}`}
                                    type="button"
                                    data-thumbnail-index={viewIndex}
                                    $active={isActive}
                                    aria-label={lnT('messages:showMedia', { value1: String(viewIndex + 1) })}
                                    aria-current={isActive ? 'true' : undefined}
                                    onMouseEnter={() => {
                                        showIndexImmediately(viewIndex);
                                    }}
                                    onClick={event => {
                                        stopEvent(event);
                                        startTransitionToIndex(viewIndex);
                                    }}
                                >
                                    {thumbnailSrc ? (
                                        <ThumbnailImage src={thumbnailSrc} alt="" loading="lazy" />
                                    ) : (
                                        <ThumbnailFallback>{viewIndex + 1}</ThumbnailFallback>
                                    )}
                                    {view?.type === 'video' ? <ThumbnailVideoBadge>{lnT('messages:video')}</ThumbnailVideoBadge> : null}
                                </ThumbnailButton>
                            );
                        })}
                    </ThumbnailRail>
                ) : null}

                {activeViewIsImage && (activeOcrText || allOcrText || onExtractAllOcr) ? (
                    <BottomRightOverlay
                        $hasThumbnailRail={hasThumbnailRail}
                        onClick={event => { event.stopPropagation?.(); }}
                        onMouseDown={event => { event.stopPropagation?.(); }}
                    >
                        {activeOcrText ? (
                            <TopActionButton
                                type="button"
                                $disabled={ocrCopyState === 'working'}
                                aria-disabled={ocrCopyState === 'working'}
                                onClick={event => {
                                    event.stopPropagation?.();
                                    handleCopyOcr();
                                }}
                                onMouseDown={event => { event.stopPropagation?.(); }}
                                aria-label={lnT('messages:copyImageText')}
                            >
                                <LuScanText size={16} />
                                <span>
                                    {ocrCopyState === 'working'
                                        ? lnT('messages:copying')
                                        : ocrCopyState === 'done'
                                            ? lnT('messages:copied')
                                            : ocrCopyState === 'error'
                                                ? lnT('messages:copyFailed')
                                                : lnT('messages:copyImageText')}
                                </span>
                            </TopActionButton>
                        ) : null}
                        {safeViews.length > 1 && allOcrText ? (
                            <TopActionButton
                                type="button"
                                $disabled={allOcrCopyState === 'working'}
                                aria-disabled={allOcrCopyState === 'working'}
                                onClick={event => {
                                    event.stopPropagation?.();
                                    handleCopyAllOcr();
                                }}
                                onMouseDown={event => { event.stopPropagation?.(); }}
                                aria-label={lnT('messages:copyTextFromAllCarouselImages')}
                            >
                                <LuScanText size={16} />
                                <span>
                                    {allOcrCopyState === 'working'
                                        ? lnT('messages:copyingAll')
                                        : allOcrCopyState === 'done'
                                            ? lnT('messages:allTextCopied')
                                            : allOcrCopyState === 'error'
                                                ? lnT('messages:copyFailed')
                                                : lnT('messages:copyAllImageText')}
                                </span>
                            </TopActionButton>
                        ) : null}
                        {onExtractAllOcr && !allOcrExtracted ? (
                            <TopActionButton
                                type="button"
                                $disabled={extractingAllOcr}
                                aria-disabled={extractingAllOcr}
                                onClick={event => {
                                    event.stopPropagation?.();
                                    onExtractAllOcr();
                                }}
                                onMouseDown={event => { event.stopPropagation?.(); }}
                                aria-label={lnT('messages:extractTextFromAllCarouselImages')}
                            >
                                <LuScanText size={16} />
                                <span>
                                    {extractingAllOcr ? lnT('messages:addingTextToMessage') : lnT('messages:extractAllImageText')}
                                </span>
                            </TopActionButton>
                        ) : null}
                    </BottomRightOverlay>
                ) : null}
            </LightboxShell>
        </Modal>
    );
}

const shellEnter = keyframes`
    from {
        opacity: 0;
        transform: translate3d(0, 40px, 0) scale(0.96);
    }

    to {
        opacity: 1;
        transform: translate3d(0, 0, 0) scale(1);
    }
`;

const LightboxShell = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    color: rgba(255, 255, 255, 0.95);
    background: transparent;
    animation: ${shellEnter} 300ms ${ENTER_EASING};
`;

const TopBar = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    pointer-events: auto;
`;

const BottomRightOverlay = styled.div`
    position: absolute;
    bottom: ${props => (props.$hasThumbnailRail ? '112px' : '20px')};
    right: 20px;
    z-index: 20;
    pointer-events: auto;
`;

const CounterLabel = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.72);
    pointer-events: auto;
`;

const TopBarActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    pointer-events: auto;
`;

const TopActionButton = styled.button`
    min-height: 42px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(0, 0, 0, 0.38);
    color: rgba(255, 255, 255, 0.92);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 14px;
    cursor: pointer;
    pointer-events: auto;
    transition: background-color 200ms ease, opacity 200ms ease;

    &:hover {
        background: ${props => (props.$disabled ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.16)')};
    }

    opacity: ${props => (props.$disabled ? 0.6 : 1)};
    cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
`;

const CloseButton = styled.button`
    width: 42px;
    height: 42px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(0, 0, 0, 0.38);
    color: rgba(255, 255, 255, 0.92);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    pointer-events: auto;
    transition: background-color 200ms ease, opacity 200ms ease;

    &:hover {
        background: rgba(255, 255, 255, 0.16);
    }
`;

const Stage = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding-bottom: ${props => (props.$hasThumbnailRail ? '104px' : 0)};
`;

const SlideViewport = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const SlidesStack = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const SlideContent = styled.div`
    width: 100%;
    height: 100%;
`;

const SlidePane = styled.div`
    position: ${props => (props.$role === 'current' ? 'relative' : 'absolute')};
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: ${props => {
        if (props.$role === 'current') {
            return 1;
        }

        if (props.$role === 'incoming') {
            return props.$phase === 'active' ? 1 : 0;
        }

        return props.$phase === 'active' ? 0 : 1;
    }};
    transform: ${props => {
        if (props.$role === 'current') {
            return 'translate3d(0, 0, 0)';
        }

        const offset = `${props.$direction * SLIDE_OFFSET_PX}px`;
        if (props.$role === 'incoming') {
            return props.$phase === 'active'
                ? 'translate3d(0, 0, 0)'
                : `translate3d(${offset}, 0, 0)`;
        }

        return props.$phase === 'active'
            ? `translate3d(${props.$direction * -SLIDE_OFFSET_PX}px, 0, 0)`
            : 'translate3d(0, 0, 0)';
    }};
    transition:
        transform ${SLIDE_ANIMATION_MS}ms ${ENTER_EASING},
        opacity ${SLIDE_ANIMATION_MS}ms ${ENTER_EASING};
    will-change: transform, opacity;
`;

const NavLayer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 3;
    pointer-events: none;
`;

const NavButton = styled.button`
    position: absolute;
    pointer-events: auto;
    top: 50%;
    ${props => props.$side}: 18px;
    transform: translateY(-50%);
    z-index: 4;
    width: 48px;
    height: 48px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(0, 0, 0, 0.38);
    color: rgba(255, 255, 255, 0.92);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 200ms ease, transform 200ms ease;

    &:hover {
        background: rgba(255, 255, 255, 0.22);
    }
`;

const SlideImage = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    width: auto;
    height: auto;
    user-select: none;
`;

const SlideVideoWrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SlideVideo = styled.video`
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    background: #000;
`;

const ThumbnailRail = styled.div`
    position: absolute;
    left: 50%;
    bottom: 20px;
    transform: translateX(-50%);
    z-index: 21;
    max-width: calc(100vw - 48px);
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 14px;
    background: rgba(0, 0, 0, 0.42);
    overflow-x: auto;
    pointer-events: auto;
    scrollbar-width: thin;
`;

const ThumbnailButton = styled.button`
    position: relative;
    flex: 0 0 auto;
    width: 58px;
    height: 58px;
    border-radius: 8px;
    border: 2px solid ${props => (props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.18)')};
    padding: 0;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.08);
    opacity: ${props => (props.$active ? 1 : 0.76)};
    cursor: pointer;
    transition: border-color 160ms ease, opacity 160ms ease, transform 160ms ease;

    &:hover {
        opacity: 1;
        transform: translateY(-1px);
    }
`;

const ThumbnailImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const ThumbnailFallback = styled.span`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.84);
    font-size: 12px;
    font-weight: 700;
`;

const ThumbnailVideoBadge = styled.span`
    position: absolute;
    left: 4px;
    bottom: 4px;
    padding: 2px 4px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.62);
    color: #ffffff;
    font-size: 8px;
    font-weight: 700;
    line-height: 1;
`;


const UnavailableState = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.78);
    font-size: 14px;
    letter-spacing: 0.01em;
`;

export default MediaLightboxModal;
